// ABOUTME: Avatar customization state
// ABOUTME: Manages avatar appearance, equipped items, and unlocks using React Context

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { BodyType, FacialHair, HairColor, HairStyle, SkinTone } from '@/constants/avatarAssets'

/**
 * Avatar appearance configuration
 */
export interface AvatarAppearance {
  bodyType: BodyType
  skinTone: SkinTone
  hairStyle: HairStyle
  hairColor: HairColor
  facialHair: FacialHair
}

/**
 * Equipped avatar items by category
 */
export interface EquippedItems {
  tops: string | null
  bottoms: string | null
  shoes: string | null
  outerwear: string | null
  accessories: string[]
}

/**
 * Avatar state
 */
interface AvatarState {
  appearance: AvatarAppearance
  equipped: EquippedItems
  unlockedItems: string[]
  savedOutfits: Array<{
    id: string
    name: string
    equipped: EquippedItems
    createdAt: Date
  }>
}

/**
 * Avatar actions
 */
interface AvatarActions {
  setBodyType: (bodyType: BodyType) => void
  setSkinTone: (skinTone: SkinTone) => void
  setHairStyle: (hairStyle: HairStyle) => void
  setHairColor: (hairColor: HairColor) => void
  setFacialHair: (facialHair: FacialHair) => void
  equipItem: (category: keyof EquippedItems, itemId: string | null) => void
  addAccessory: (itemId: string) => void
  removeAccessory: (itemId: string) => void
  clearOutfit: () => void
  unlockItem: (itemId: string) => void
  isItemUnlocked: (itemId: string) => boolean
  saveOutfit: (name: string) => void
  loadOutfit: (outfitId: string) => void
  deleteOutfit: (outfitId: string) => void
  reset: () => void
}

type AvatarContextValue = AvatarState & AvatarActions

const STORAGE_KEY = 'gentleman-avatar-store'

const defaultAppearance: AvatarAppearance = {
  bodyType: 'regular',
  skinTone: 'medium',
  hairStyle: 'short',
  hairColor: 'brown',
  facialHair: 'none',
}

const defaultEquipped: EquippedItems = {
  tops: 'tshirt-white',
  bottoms: 'jeans-blue',
  shoes: 'sneakers-white',
  outerwear: null,
  accessories: [],
}

const defaultUnlockedItems = [
  'tshirt-white',
  'tshirt-black',
  'tshirt-navy',
  'jeans-blue',
  'jeans-black',
  'shorts-khaki',
  'sneakers-white',
  'sneakers-black',
  'hoodie-gray',
  'belt-brown',
]

const initialState: AvatarState = {
  appearance: defaultAppearance,
  equipped: defaultEquipped,
  unlockedItems: defaultUnlockedItems,
  savedOutfits: [],
}

function loadPersistedState(): AvatarState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return { ...initialState, ...parsed }
    }
  } catch {}
  return initialState
}

function persistState(state: AvatarState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {}
}

const AvatarContext = createContext<AvatarContextValue | null>(null)

export function AvatarStoreProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AvatarState>(loadPersistedState)

  const update = useCallback((updater: (prev: AvatarState) => AvatarState) => {
    setState((prev) => {
      const next = updater(prev)
      persistState(next)
      return next
    })
  }, [])

  const setBodyType = useCallback(
    (bodyType: BodyType) => {
      update((s) => ({ ...s, appearance: { ...s.appearance, bodyType } }))
    },
    [update]
  )

  const setSkinTone = useCallback(
    (skinTone: SkinTone) => {
      update((s) => ({ ...s, appearance: { ...s.appearance, skinTone } }))
    },
    [update]
  )

  const setHairStyle = useCallback(
    (hairStyle: HairStyle) => {
      update((s) => ({ ...s, appearance: { ...s.appearance, hairStyle } }))
    },
    [update]
  )

  const setHairColor = useCallback(
    (hairColor: HairColor) => {
      update((s) => ({ ...s, appearance: { ...s.appearance, hairColor } }))
    },
    [update]
  )

  const setFacialHair = useCallback(
    (facialHair: FacialHair) => {
      update((s) => ({ ...s, appearance: { ...s.appearance, facialHair } }))
    },
    [update]
  )

  const equipItem = useCallback(
    (category: keyof EquippedItems, itemId: string | null) => {
      if (category === 'accessories') return
      update((s) => ({ ...s, equipped: { ...s.equipped, [category]: itemId } }))
    },
    [update]
  )

  const addAccessory = useCallback(
    (itemId: string) => {
      update((s) => {
        if (s.equipped.accessories.includes(itemId)) return s
        if (s.equipped.accessories.length >= 3) return s
        return {
          ...s,
          equipped: { ...s.equipped, accessories: [...s.equipped.accessories, itemId] },
        }
      })
    },
    [update]
  )

  const removeAccessory = useCallback(
    (itemId: string) => {
      update((s) => ({
        ...s,
        equipped: {
          ...s.equipped,
          accessories: s.equipped.accessories.filter((id) => id !== itemId),
        },
      }))
    },
    [update]
  )

  const clearOutfit = useCallback(() => {
    update((s) => ({
      ...s,
      equipped: { tops: null, bottoms: null, shoes: null, outerwear: null, accessories: [] },
    }))
  }, [update])

  const unlockItem = useCallback(
    (itemId: string) => {
      update((s) => {
        if (s.unlockedItems.includes(itemId)) return s
        return { ...s, unlockedItems: [...s.unlockedItems, itemId] }
      })
    },
    [update]
  )

  const isItemUnlocked = useCallback(
    (itemId: string) => state.unlockedItems.includes(itemId),
    [state.unlockedItems]
  )

  const saveOutfit = useCallback(
    (name: string) => {
      update((s) => ({
        ...s,
        savedOutfits: [
          ...s.savedOutfits,
          { id: crypto.randomUUID(), name, equipped: { ...s.equipped }, createdAt: new Date() },
        ],
      }))
    },
    [update]
  )

  const loadOutfit = useCallback(
    (outfitId: string) => {
      update((s) => {
        const outfit = s.savedOutfits.find((o) => o.id === outfitId)
        if (!outfit) return s
        return { ...s, equipped: { ...outfit.equipped } }
      })
    },
    [update]
  )

  const deleteOutfit = useCallback(
    (outfitId: string) => {
      update((s) => ({ ...s, savedOutfits: s.savedOutfits.filter((o) => o.id !== outfitId) }))
    },
    [update]
  )

  const reset = useCallback(() => {
    setState(initialState)
    persistState(initialState)
  }, [])

  const value = useMemo<AvatarContextValue>(
    () => ({
      ...state,
      setBodyType,
      setSkinTone,
      setHairStyle,
      setHairColor,
      setFacialHair,
      equipItem,
      addAccessory,
      removeAccessory,
      clearOutfit,
      unlockItem,
      isItemUnlocked,
      saveOutfit,
      loadOutfit,
      deleteOutfit,
      reset,
    }),
    [
      state,
      setBodyType,
      setSkinTone,
      setHairStyle,
      setHairColor,
      setFacialHair,
      equipItem,
      addAccessory,
      removeAccessory,
      clearOutfit,
      unlockItem,
      isItemUnlocked,
      saveOutfit,
      loadOutfit,
      deleteOutfit,
      reset,
    ]
  )

  return <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>
}

export function useAvatarStore(): AvatarContextValue {
  const ctx = useContext(AvatarContext)
  if (!ctx) throw new Error('useAvatarStore must be used within AvatarStoreProvider')
  return ctx
}
